// industryDetails.js
// Per-industry rich data: hero image, narrative, KPIs, challenges, applicable
// service slugs (must match values from mockData.js services[].slug) and
// representative case-study snippets.
//
// Keyed by industry slug — same slug as in mockData.industries[].

export const INDUSTRY_DETAILS = {
  'smart-cities': {
    hero: 'https://images.unsplash.com/photo-1483653364400-eedcfb9f1f88?w=1600&h=900&fit=crop',
    tagline: 'Digital twins · GIS · IoT-ready infrastructure',
    summary: 'Master-planned communities and smart-city programs depend on coordinated GIS, BIM and traffic data. We deliver the geospatial backbone, utility plans, digital-twin models and analytics dashboards that bring connected cities to life.',
    globalOps: 'Smart-city initiatives now span four continents — from European 15-minute-city retrofits to ASEAN greenfield mega-developments, North-American suburban densification and Gulf-region net-zero districts. Land Drafters supports the end-to-end pipeline: parcel-level GIS, federated digital twins, IoT-ready utility layouts and traffic-impact studies, working in clients\' local CRS, language and code stack from our distributed US and India delivery hubs.',
    challenges: [
      'Heterogeneous data sources (CAD, GIS, IoT, satellite) requiring federation',
      'Stringent municipal codes and zoning compliance across jurisdictions',
      'Coordination between transit, utilities, public space and emergency planning',
      'Need for digital twin parity with as-built reality'
    ],
    deliverables: [
      'Geospatial master plans and zoning overlays',
      'Smart-utility & lighting layout drawings',
      'BIM-to-GIS federation for digital twins',
      '3D city models for stakeholder visualisation',
      'Traffic-impact and pedestrian-flow studies'
    ],
    services: ['gis-geospatial', 'land-development', '3d-modeling', 'bim-services', 'transportation', 'civil-engineering', 'rendering-animation'],
    stats: [
      { val: '12+', label: 'Smart-city programs' },
      { val: '320K', label: 'Parcels mapped' },
      { val: '4', label: 'Continents served' }
    ]
  },

  'real-estate': {
    hero: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1600&h=900&fit=crop',
    tagline: 'Site plans · Renderings · Permit packages',
    summary: 'For residential and commercial developers we deliver permit-ready architectural drawings, marketing-grade renderings, site plans and BIM models that move projects from concept to sales velocity.',
    globalOps: 'Residential and commercial real-estate is a $4 trillion global industry that moves on speed. Land Drafters\' two-shift global delivery (Apex, NC + Pune, IN) gives developers, REITs and architects 24-hour-turnaround permit sets, marketing renderings and brochures across North America, Europe, the Middle East and Australia — at up to 54% lower cost than onshore-only teams.',
    challenges: [
      'Tight pre-sales timelines requiring rapid visualisation',
      'Multi-stakeholder approval (architect, owner, marketing, city)',
      'Permit-package compliance with local building codes',
      'Maintaining material/cost consistency across phases'
    ],
    deliverables: [
      'Photo-realistic exterior & interior renderings',
      'Marketing brochures and flythrough animations',
      'Sales-floor plans & unit-mix presentations',
      'Permit-ready architectural drawing sets',
      'BIM models for FM hand-over'
    ],
    services: ['architectural', 'rendering-animation', '3d-modeling', 'bim-services', 'land-development', 'civil-engineering', 'graphics-services'],
    stats: [
      { val: '8K+', label: 'Units modelled' },
      { val: '2,500', label: 'Renderings/yr' },
      { val: '48 hr', label: 'Avg. TAT' }
    ]
  },

  construction: {
    hero: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&h=900&fit=crop',
    tagline: 'Shop drawings · Construction docs · As-builts',
    summary: 'From bid drawings to as-builts we are the off-site engineering team that keeps general contractors and specialty subs ahead of schedule. ISO 9001:2015 process and 3-stage QC means zero re-work surprises.',
    globalOps: 'From US DOT highway packages to UAE high-rise shop drawings and Australian residential builders, construction firms partner with Land Drafters as their extended drafting and BIM-VDC arm. We follow each client\'s CAD standards, work in their preferred Revit/AutoCAD environment, and absorb workload peaks without the overhead of new hires.',
    challenges: [
      'RFI overload between trades on critical-path activities',
      'Frequent design revisions during construction',
      'Clash-free shop drawings across multiple subs',
      'Tight QC for stamping & PE approval'
    ],
    deliverables: [
      'Permit-set drawings and stamped construction docs',
      'Shop drawings for steel, rebar, precast, glazing, MEP',
      'Coordination drawings & clash reports',
      'As-built drawings from point clouds & redlines',
      'Construction-phase BIM-VDC models'
    ],
    services: ['steel-detailing', 'bim-services', 'construction-engineering', 'mep-engineering', 'structural-engineering', 'architectural', 'lumber-millwork'],
    stats: [
      { val: '15K+', label: 'Projects delivered' },
      { val: '3-stage', label: 'Quality check' },
      { val: '24h', label: 'RFI response' }
    ]
  },

  aec: {
    hero: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1600&h=900&fit=crop',
    tagline: 'Concept · BIM · MEP · Structural · CA support',
    summary: 'Full A&E firms partner with us as their extended design studio. We slot into your Revit/AutoCAD workflows, follow your CAD standards, and scale up or down to absorb project peaks without overhead.',
    globalOps: 'Architecture & Engineering firms across 40+ countries rely on Land Drafters as their on-demand multidisciplinary team. We\'ve supported boutique American studios on heritage retrofits, EU practices on EnEV-compliant residential, and large GCC firms on multi-tower podiums — always to the client\'s own CAD standards, BIM execution plan and stamping requirements.',
    challenges: [
      'Peak workloads that exceed in-house capacity',
      'Need to maintain firm-specific CAD/BIM standards',
      'Multi-disciplinary coordination across consultants',
      'Construction-administration phase deliverables'
    ],
    deliverables: [
      'Concept-design drawings & feasibility studies',
      'Federated BIM models (LOD 200-400)',
      'Multi-discipline coordinated drawing sets',
      'CD-phase drawing production & spec sheets',
      'CA-phase RFI, submittal and as-built support'
    ],
    services: ['architectural', 'bim-services', 'mep-engineering', 'structural-engineering', 'engineering-designs', 'civil-engineering', '3d-modeling'],
    stats: [
      { val: '350+', label: 'A&E partners' },
      { val: '54%', label: 'Avg. cost saved' },
      { val: 'LOD 400', label: 'BIM capability' }
    ]
  },

  government: {
    hero: 'https://images.unsplash.com/photo-1555680202-c86f0e12f086?w=1600&h=900&fit=crop',
    tagline: 'DOT · Municipal · Public works · Compliance',
    summary: 'We support DOTs, municipalities and public agencies with code-compliant infrastructure drawings — from highways and bridges to public utilities, parks and government facilities.',
    globalOps: 'Public-sector clients — US state DOTs, UK councils, Indian municipal bodies, Middle-East planning authorities — face strict procurement timelines and code compliance. Our ISO-9001:2015 process, audit-friendly QC trail and 20-year sector record make us a trusted off-site arm for AASHTO, MUTCD, ADA, Eurocode and BS-EN deliverables.',
    challenges: [
      'Strict compliance with AASHTO, MUTCD, ADA, and local DOT manuals',
      'Public procurement timelines and audit trails',
      'Public stakeholder review cycles',
      'Maintenance of legacy as-built archives'
    ],
    deliverables: [
      'Highway, intersection and signal plans',
      'Bridge and culvert structural drawings',
      'Public-utility (water/sewer/storm) plans',
      'Park, plaza, and civic-building drawings',
      'Asset GIS inventories and as-built archives'
    ],
    services: ['civil-engineering', 'transportation', 'structural-engineering', 'gis-geospatial', 'water-wastewater', 'land-development', 'environmental-engineering'],
    stats: [
      { val: '40+', label: 'Public agencies' },
      { val: 'AASHTO', label: 'Code compliant' },
      { val: '20 yr', label: 'Sector experience' }
    ]
  },

  environmental: {
    hero: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1600&h=900&fit=crop',
    tagline: 'EIA · Hydrogeology · Soil & water analytics',
    summary: 'For environmental consultancies, mining operators and remediation firms we produce the geological cross-sections, environmental-impact maps, hydrogeology models and mine-planning CAD that withstand regulatory scrutiny.',
    globalOps: 'Environmental consultancies, mining majors and remediation firms tackle global pressures: ESG reporting, carbon-neutral pledges, mine reclamation. Land Drafters produces EIA figures, hydrogeology models, mine-planning CAD and rehabilitation drawings that withstand regulatory scrutiny from EPA, EIA, MEFCC, EU Habitats Directive and country-specific frameworks.',
    challenges: [
      'Permit and regulatory submission timelines',
      'Integration of GIS, soil, and water-quality datasets',
      'Mine-planning and reclamation drawings',
      'Stakeholder visualisation of impacts'
    ],
    deliverables: [
      'EIA report figures and impact maps',
      'Hydrogeology models and contour plots',
      'Mine pit, dump and haul-road CAD plans',
      'Reclamation and rehabilitation drawings',
      'Air/water/noise contour overlays'
    ],
    services: ['environmental-engineering', 'gis-geospatial', 'water-wastewater', 'geotechnical-engineering', 'civil-engineering', 'land-development'],
    stats: [
      { val: '600+', label: 'EIA reports' },
      { val: '15K ha', label: 'Land assessed' },
      { val: 'ISO', label: 'Audited process' }
    ]
  },

  telecom: {
    hero: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1600&h=900&fit=crop',
    tagline: '5G · Tower · Fiber · RAN as-builts',
    summary: 'Telecom carriers, tower companies and 5G integrators rely on us for tower-mount drawings, RF coverage maps, fiber routing, and high-volume RAN as-built production.',
    globalOps: 'Telecom carriers and tower companies face exploding 5G densification demand — millions of new small-cell sites globally by 2027. Land Drafters delivers tower-mount drawings, RF coverage maps and high-volume RAN as-builts at scale, with templated workflows for major US, EU and APAC carrier portfolios.',
    challenges: [
      'Scale: thousands of sites per program',
      'Standardised templates across an entire carrier portfolio',
      'Multi-sector coordination (RF, civil, structural, electrical)',
      'High-volume turnaround within hours, not days'
    ],
    deliverables: [
      'Tower elevation and mount-mapping drawings',
      'Fiber-route and conduit layouts',
      'RF coverage and propagation maps',
      'Cabinet and shelter layouts',
      'High-volume RAN as-built drawings'
    ],
    services: ['gis-geospatial', 'civil-engineering', 'structural-engineering', 'land-surveying', 'mep-engineering', '3d-modeling'],
    stats: [
      { val: '25K+', label: 'Tower sites' },
      { val: '5G ready', label: 'Carrier portfolio' },
      { val: '12 hr', label: 'Same-day TAT' }
    ]
  },

  'oil-gas': {
    hero: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1600&h=900&fit=crop',
    tagline: 'LNG · Pipelines · Refinery · PV Elite · SP3D',
    summary: 'Oil & gas operators and EPC firms turn to us for process-equipment design (PV Elite, Compress, AutoPipe), plant layouts (SP3D / Plant 3D), and pipeline routing with full code compliance.',
    globalOps: 'Oil & gas, LNG and energy projects require code-stamped engineering across ASME, API, ASTM, PED, ATEX and NACE. Our specialist teams use PV Elite, Compress, AutoPipe, CAESAR II and SmartPlant 3D to support EPC contractors and operators on greenfield builds, brownfield retrofits and turnarounds in the Gulf, North Sea, US Gulf Coast and Asia-Pacific.',
    challenges: [
      'Compliance with ASME, API, ASTM, PED, ATEX, NACE',
      'Complex multi-discipline coordination on brownfield plants',
      'Stringent QC for pressure vessels and piping',
      'Hazardous-area classification drawings'
    ],
    deliverables: [
      'Pressure-vessel & heat-exchanger design (PV Elite)',
      'Piping isometrics & stress analysis (CAESAR II)',
      '3D plant models (SmartPlant / Plant 3D)',
      'P&ID and PFD diagrams',
      'Pipeline routing and tie-in drawings'
    ],
    services: ['process-equipment', 'plant-engineering', 'oil-gas-energy', 'mechanical-engineering', 'mep-engineering', 'structural-engineering', '3d-modeling'],
    stats: [
      { val: 'ASME', label: 'Code certified' },
      { val: '1,200', label: 'Vessels designed' },
      { val: 'SP3D', label: 'Smart-plant ready' }
    ]
  },

  transportation: {
    hero: 'https://images.unsplash.com/photo-1573053009354-d2a4d0d68906?w=1600&h=900&fit=crop',
    tagline: 'Highways · Rail · Bridges · MUTCD · AASHTO',
    summary: 'DOTs and transportation consultancies rely on our highway alignments, intersection plans, traffic-engineering drawings, bridge structural work and rail-corridor designs to AASHTO and MUTCD standards.',
    globalOps: 'Highway, rail, bridge and transit projects power national economies. Land Drafters has delivered 2,800+ km of road designs, 180+ bridge structural sets and rail-corridor drawings across the US, India, the UK and Australia — always to local MUTCD/AASHTO/IRC/Eurocode standards and DOT/FHWA/Network Rail review cycles.',
    challenges: [
      'Long, linear projects with continuous coordination',
      'Strict adherence to MUTCD/AASHTO/IRC',
      'Right-of-way and easement complexity',
      'Multi-agency review (DOT, FHWA, state, county)'
    ],
    deliverables: [
      'Highway alignment & profile drawings',
      'Intersection and traffic-signal plans (MUTCD)',
      'Bridge structural and culvert drawings',
      'Pavement-marking and signage plans',
      'Rail-corridor and station-area drawings'
    ],
    services: ['transportation', 'civil-engineering', 'structural-engineering', 'land-surveying', 'gis-geospatial', 'geotechnical-engineering', 'land-development'],
    stats: [
      { val: '2,800 km', label: 'Roads designed' },
      { val: '180+', label: 'Bridges' },
      { val: 'AASHTO', label: 'Code compliant' }
    ]
  },

  water: {
    hero: 'https://images.unsplash.com/photo-1559825481-12a05cc00344?w=1600&h=900&fit=crop',
    tagline: 'Water supply · Sewer · Storm · HEC-RAS · HEC-HMS',
    summary: 'Utility districts, municipalities and water consultants partner with us for water-supply, sewer, drainage and storm-water designs with HEC-RAS / HEC-HMS hydraulic modelling.',
    globalOps: 'Water-supply, sewer and storm-drainage infrastructure underpins every urban region. From EPA-compliant US municipal systems to monsoon-resilient South-Asian drainage and arid-climate Gulf desal networks, we model hydraulics with HEC-RAS / HEC-HMS, draft collection systems and design pump stations to the client\'s local code stack.',
    challenges: [
      'Floodplain analysis and HEC-RAS modelling',
      'Sewer-shed and storm-drainage capacity',
      'Pressure-zone and pumping station design',
      'Compliance with SWPPP, NPDES, EPA standards'
    ],
    deliverables: [
      'Water-supply network drawings & hydraulic models',
      'Storm-water management plans (SWPPP)',
      'Sewer-collection and force-main drawings',
      'Pump-station and reservoir design',
      'HEC-RAS / HEC-HMS hydraulic modelling'
    ],
    services: ['water-wastewater', 'civil-engineering', 'mep-engineering', 'gis-geospatial', 'environmental-engineering', 'land-development', 'structural-engineering'],
    stats: [
      { val: '450+', label: 'Water projects' },
      { val: 'HEC-RAS', label: 'Hydraulic modelling' },
      { val: 'EPA', label: 'Compliance ready' }
    ]
  },

  industrial: {
    hero: 'https://images.unsplash.com/photo-1581092335331-5e00ac65e934?w=1600&h=900&fit=crop',
    tagline: 'Plant layouts · Piping ISO · Equipment · Steel detailing',
    summary: 'Industrial plants — pharma, food, paper, chemical — depend on coordinated plant layouts, equipment placement, piping isometrics and steel-detailing. We deliver end-to-end CAD support for greenfield builds and brownfield retrofits.',
    globalOps: 'Industrial plants — pharma, food, paper, chemical, automotive — are upgrading globally for digitalisation and decarbonisation. We deliver coordinated 2D/3D plant layouts, piping isometrics, steel platforms, clean-room and HVAC drawings on Plant 3D and SmartPlant from concept through turnover.',
    challenges: [
      'Dense equipment layouts with strict clearance requirements',
      'Piping isometrics with hundreds of revisions',
      'Steel-detailing of conveyors, platforms, racks',
      'Hazardous-area and clean-room compliance'
    ],
    deliverables: [
      'Plant layout drawings (2D & 3D)',
      'Piping isometrics and BOMs',
      'Equipment GA drawings',
      'Steel platforms, racks, conveyor frames',
      'HVAC, clean-room and utility distribution'
    ],
    services: ['plant-engineering', 'process-equipment', 'steel-detailing', 'mechanical-engineering', 'mep-engineering', 'manufacturing', 'structural-engineering'],
    stats: [
      { val: '600+', label: 'Plant projects' },
      { val: 'Plant 3D', label: 'Software stack' },
      { val: 'GMP', label: 'Pharma ready' }
    ]
  },

  renewable: {
    hero: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1600&h=900&fit=crop',
    tagline: 'Solar · Wind · Grid-tie · Foundation · BoS',
    summary: 'Renewable-energy developers and EPCs use our solar-farm CAD layouts, wind-turbine foundation drawings, grid-tie and SLD diagrams, and balance-of-system documentation.',
    globalOps: 'Solar and wind capacity is being added at a record clip — over 500 GW globally in 2025. Land Drafters supports developers and EPCs with site-specific array layouts, single-line diagrams, foundation designs and grid-tie drawings for solar farms, wind parks and grid-scale battery storage from the US Sun Belt to the Gulf and APAC.',
    challenges: [
      'Site-specific solar irradiance and wind-resource modelling',
      'Turbine-foundation geotechnical & structural design',
      'Grid-tie and substation single-line drawings',
      'Permit & interconnection paperwork'
    ],
    deliverables: [
      'Solar array layouts, string diagrams, SLDs',
      'Wind-turbine foundation designs',
      'Substation & grid-tie drawings',
      'Battery-storage layout & BoS',
      'Cable-routing and trenching plans'
    ],
    services: ['oil-gas-energy', 'civil-engineering', 'structural-engineering', 'mep-engineering', 'gis-geospatial', 'land-development', 'geotechnical-engineering'],
    stats: [
      { val: '3.2 GW', label: 'Solar designed' },
      { val: '120+', label: 'Wind foundations' },
      { val: 'NEC', label: 'Grid-code ready' }
    ]
  },

  'residential-commercial': {
    hero: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1600&h=900&fit=crop',
    tagline: 'Floor plans · Permit drawings · 3D renders',
    summary: 'Builders, developers and architects get fast, accurate floor plans, elevations, permit packages and marketing renderings — overnight turnaround on standard residential work.',
    globalOps: 'Builders and developers run high-volume residential and commercial programs across diverse jurisdictions. Land Drafters\' templated workflows produce permit-ready plans, elevations and 3D renderings overnight, scaling from one-off custom homes to multi-thousand-unit master plans across the US, GCC, and APAC.',
    challenges: [
      'Volume production with consistent CAD standards',
      'Permit-set turnaround in tight timelines',
      'Marketing-grade renderings on schedule',
      'Code compliance across multiple jurisdictions'
    ],
    deliverables: [
      '2D floor plans, elevations, sections',
      'Permit packages with notes & schedules',
      'Photo-realistic 3D renderings and walkthroughs',
      'Marketing brochures and color boards',
      'BIM models for builders & FM'
    ],
    services: ['architectural', 'rendering-animation', '3d-modeling', 'bim-services', 'civil-engineering', 'graphics-services', 'lumber-millwork'],
    stats: [
      { val: '20K+', label: 'Floor plans' },
      { val: '5K+', label: 'Renderings' },
      { val: '24 hr', label: 'Permit TAT' }
    ]
  },

  'bim-gis': {
    hero: 'https://images.unsplash.com/photo-1654230163544-b69049014b60?w=1600&h=900&fit=crop',
    tagline: 'BIM federation · GIS analytics · Digital twins',
    summary: 'Owners, operators and digital-twin platforms partner with us to federate BIM, GIS and IoT data, run clash detection, and stand up digital twins that mirror reality.',
    globalOps: 'Owner-operators, smart-city platforms and infrastructure asset-management programs are unifying BIM, GIS and IoT into digital twins. Land Drafters bridges the data — federating Revit/IFC with ArcGIS/QGIS, running clash detection on Navisworks/BIM 360, and standing up digital-twin dashboards with IoT integration for 40+ countries.',
    challenges: [
      'Federation of heterogeneous BIM, GIS, IoT, satellite data',
      'Clash-detection on multi-discipline models',
      'Digital-twin synchronisation with FM systems',
      'Big-data visualisation for stakeholders'
    ],
    deliverables: [
      'Federated BIM (Revit, IFC) + GIS (ArcGIS, QGIS) datasets',
      'Clash detection and resolution (Navisworks, BIM 360)',
      'Digital-twin models with IoT integration',
      'GIS dashboards and spatial analytics',
      '3D scan-to-BIM and as-built modelling'
    ],
    services: ['bim-services', 'gis-geospatial', '3d-modeling', 'engineering-designs', 'rendering-animation', 'facility-management'],
    stats: [
      { val: 'LOD 400', label: 'BIM capability' },
      { val: '40K+', label: 'GIS layers' },
      { val: 'IFC 4', label: 'Open BIM ready' }
    ]
  }
};
